import { format, parseISO } from 'date-fns'
import { nl } from 'date-fns/locale'

interface Datumweek {
  maandag: string
}

interface Niveauweek {
  niveau?: string | null
}

export interface Maandgroep {
  label: string
  span: number
}

export interface Niveaufase {
  niveau: string | null
  span: number
  weken: number
}

interface Reeks<S> {
  sleutel: S
  vanaf: number
  aantal: number
}

function groepeerOpeenvolgend<T, S>(items: readonly T[], sleutelVan: (item: T) => S): Reeks<S>[] {
  const reeksen: Reeks<S>[] = []
  items.forEach((item, index) => {
    const sleutel = sleutelVan(item)
    const laatste = reeksen.at(-1)
    if (laatste && laatste.sleutel === sleutel) {
      laatste.aantal += 1
      return
    }
    reeksen.push({ sleutel, vanaf: index, aantal: 1 })
  })
  return reeksen
}

export function maandgroepen(weken: readonly Datumweek[]): Maandgroep[] {
  return groepeerOpeenvolgend(weken, (week) => format(parseISO(week.maandag), 'yyyy-MM')).map(
    (reeks) => ({
      label: format(parseISO(weken[reeks.vanaf].maandag), 'LLL', { locale: nl }),
      span: reeks.aantal,
    }),
  )
}

export function heeftNiveaus(weken: readonly Niveauweek[]): boolean {
  return weken.some((week) => (week.niveau ?? '') !== '')
}

/**
 * Een vakantie- of tussenweek draagt geen niveau, maar breekt de fase niet af zolang
 * ervoor en erna hetzelfde niveau staat. Alleen een gat tussen twee verschillende
 * fasen, of aan het begin of eind van de reeks, wordt een eigen naamloos vak.
 */
export function niveaufasen(weken: readonly Niveauweek[]): Niveaufase[] {
  const doorlopend = weken.map((week, index) => week.niveau ?? overbrugdNiveau(weken, index))

  return groepeerOpeenvolgend(doorlopend, (niveau) => niveau).map((reeks) => ({
    niveau: reeks.sleutel,
    span: reeks.aantal,
    weken:
      reeks.sleutel === null
        ? reeks.aantal
        : weken
            .slice(reeks.vanaf, reeks.vanaf + reeks.aantal)
            .filter((week) => week.niveau === reeks.sleutel).length,
  }))
}

/**
 * De fasen van een reeks lopen in de tijd van laag naar hoog niveau, dus de volgorde
 * van eerste voorkomen is meteen de rang. Er zijn vier tinten; wat daarboven komt
 * houdt de laatste.
 */
export function niveaurangen(fasen: readonly Niveaufase[]): Map<string, number> {
  const rangen = new Map<string, number>()

  for (const fase of fasen) {
    if (fase.niveau === null || rangen.has(fase.niveau)) continue
    rangen.set(fase.niveau, Math.min(rangen.size + 1, 4))
  }

  return rangen
}

function overbrugdNiveau(weken: readonly Niveauweek[], index: number): string | null {
  const ervoor = dichtstbijzijndeNiveau(weken, index, -1)
  const erna = dichtstbijzijndeNiveau(weken, index, 1)
  return ervoor !== null && ervoor === erna ? ervoor : null
}

function dichtstbijzijndeNiveau(
  weken: readonly Niveauweek[],
  index: number,
  richting: 1 | -1,
): string | null {
  for (let stap = index + richting; stap >= 0 && stap < weken.length; stap += richting) {
    const niveau = weken[stap].niveau
    if (niveau) return niveau
  }
  return null
}
